import { useEffect, useId, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Activity, ArrowUpRight, ChevronRight, Monitor, Plus, Smartphone } from "lucide-react";
import { Badge, Button, Dialog, DialogActions, Field, FilterBar, SearchField, Select, SummaryMetrics, Table, Toast } from "@studio";
import { AdminActiveRuns } from "./active-runs";
import { CustomerDetailDialog, useCustomerDetailFocus } from "./customer-detail";
import { MachineDetailDialog } from "./machine-detail";
import { ReleaseManagementScreen } from "./release";
import { api, errorMessage } from "../lib/api";
import { can, isBillingOnly } from "../lib/access";
import { urlParam } from "../console/metrics";
import { useAuth } from "../lib/auth";
import { compact, fmtDate, fmtDateTime, fmtTime, initials, num, serviceNames, serviceTone } from "../lib/format";
import {
  keys,
  useAction,
  useAdminAudit,
  useAdminMachines,
  useActiveTasks,
  useAdminRuns,
  useCustomers,
  useOverview,
  usePlans,
  useStaff,
  useTemplates,
} from "../lib/queries";
import type { CustomerListItem, Me, Member } from "../lib/types";
import { ConsoleShell } from "../console/Shell";
import { Empty, ErrorBox, InfoRow, Loading, NotReady, Panel, StatGrid, StatusBadge } from "../console/ui";
import { JobTitleField, jobTitleOf } from "../console/job-title";
import { AddMemberDialog, AuditTable, ProfileScreen } from "./customer";
import { FilteredMachineCards } from "./machines";
import { SettingsScreen } from "./settings";
import { useSeoFocus } from "./seo";
import { SeoAnalyticsScreen, SeoComparisonScreen } from "./analytics";
import { CompetitionScreen } from "./competition";
import { AssignRolesDialog, RoleChips, RolesScreen, useGlobalRoles } from "./roles";
import { ManuscriptLibraryScreen } from "./manuscripts";
import { LlmKeysScreen, LlmUsageScreen } from "./llm-keys";
import { RefineScreen } from "./refine";
import { GuidelinesScreen } from "./guidelines";
import { AiExecutionScreen } from "./ai-execution";
import { SeoCollectionScreen, SeoCollectorSettings } from "./seo-ops";
import { useSeoCollectSummary } from "../lib/seo-api";
import { AdminBillingScreen } from "./billing";
import { AdminMonitoringScreen, AdminUsageCompare } from "./monitoring";
import { ACTIVE_FILTER, jobTitle, RunDetailDialog, runFilterParams, RunTable, useRunExtraFilters } from "./runs";
import { publishLabel } from "../lib/format";
import { CreateTaskDialog } from "./create-task";
import { ResultReviewScreen, ReviewPolicyPanel, useReviewFocus } from "./review";
import { DataHeading } from "../console/freshness";
import { MaskedValue } from "../console/masked";

export function AdminPage({ me, screen }: { me: Me; screen: string }) {
  const navigate = useNavigate();
  const [run, setRun] = useState<{ id: string; customerId: string | null; back?: () => void } | null>(null);
  const setRunId = (id: string | null, customerId: string | null = null, back?: () => void) => setRun(id ? { id, customerId, back } : null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  // 시안 06a1acb: 고객사·머신은 목록 → 공통 상세 다이얼로그. 다른 화면(SEO 수집·결제·상황판)도 같은 다이얼로그를 연다.
  const detail = useCustomerDetailFocus();
  const [machine, setMachine] = useState<{ customerId: string; machineId: string; label?: string; customerStatus: string; back?: boolean; fromRun?: boolean } | null>(null);
  // 진행 중이던 실행 상세를 닫으면 알린다 (시안 c9ee685 closeRun).
  const [toast, setToast] = useState("");
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(""), 5000);
    return () => clearTimeout(id);
  }, [toast]);
  const customers = useCustomers();
  const seoFocus = useSeoFocus();
  useEffect(() => {
    if (seoFocus.customerId) {
      detail.open(seoFocus.customerId, "SEO", seoFocus.tab ?? "연결");
      seoFocus.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seoFocus.customerId]);
  // 2026-10-21 요청 25: 전체 디바이스 화면은 없어졌다 → 머신 목록(모니터링 › 머신)으로
  useEffect(() => {
    if (screen === "devices") void navigate({ to: "/admin/$screen", params: { screen: "machines" }, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);
  // 옛 화면 ID 는 고객사 목록(상세)로 보낸다: seo-management → 상세 SEO 탭, sanctions → 상세 상태·제재.
  useEffect(() => {
    if (screen === "seo-management" || screen === "sanctions") {
      const first = customers.data?.items[0];
      if (first) detail.open(first.id, screen === "sanctions" ? "상태·제재" : "SEO");
      void navigate({ to: "/admin/$screen", params: { screen: "customers" }, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, customers.data]);
  const openMachine = (m: { id: string; customerId: string; label?: string; status?: string }, back = false) => {
    const c = (customers.data?.items ?? []).find((x) => x.id === m.customerId);
    setMachine({ customerId: m.customerId, machineId: m.id, label: m.label, customerStatus: c?.status ?? "active", back });
  };
  // 시안 e4ed46c: 등록 동작은 화면마다 우측 하단 FAB 하나. 전체 관리의 작업 등록은 실행 고객사를 고른다.
  const fab =
    ["overview", "runs"].includes(screen) && can(me, "TASK_CREATE")
      ? { label: "새 작업", onClick: () => setCreateOpen(true) }
      : screen === "customers" && can(me, "CUSTOMER_MANAGE")
        ? { label: "고객사 등록", onClick: () => setRegisterOpen(true) }
        : screen === "staff" && can(me, "USER_MANAGE")
          ? { label: "관리자 초대", onClick: () => setStaffOpen(true) }
          : undefined;
  const enter = (id: string, s = "overview") => void navigate({ to: "/c/$customerId/$screen", params: { customerId: id, screen: s } });
  // search: KPI 카드가 넘기는 첫 필터 (?status=failed 등, 2026-10-21). 받는 화면이 처음 열릴 때 urlParam 으로 읽는다.
  const go = (s: string, search?: Record<string, string>) => void navigate({ to: "/admin/$screen", params: { screen: s }, ...(search ? { search: search as never } : {}) });
  return (
    <ConsoleShell me={me} scope="admin" screen={screen} title={screen === "profile" ? "내 프로필" : undefined} fab={fab}>
      {screen === "overview" ? (
        <AdminOverview me={me} go={go} onOpenRun={setRunId} onOpenCustomer={(id, tab) => detail.open(id, tab)} onOpenMachine={(m) => openMachine(m)} />
      ) : screen === "customers" || screen === "seo-management" || screen === "sanctions" ? (
        <CustomersScreen enter={enter} registerOpen={registerOpen} setRegisterOpen={setRegisterOpen} onDetail={(id) => detail.open(id)} />
      ) : screen === "runs" ? (
        <AdminRuns onOpenRun={(id, customerId) => setRunId(id, customerId)} />
      ) : screen === "reviews" ? (
        <ResultReviewScreen me={me} />
      ) : screen === "seo-comparison" ? (
        <SeoComparisonScreen onSelect={(id) => detail.open(id, "SEO", "키워드")} />
      ) : screen === "competition" ? (
        <CompetitionScreen me={me} />
      ) : screen === "seo-collection" ? (
        <SeoCollectionScreen me={me} onOpenCustomer={(customerId) => detail.open(customerId, "SEO", "연결")} />
      ) : screen === "marketing" ? (
        <SeoAnalyticsScreen customerId={null} admin staffView onManage={(id) => detail.open(id, "SEO", "키워드")} />
      ) : screen === "collections" ? (
        <NotReady title="수집 이력">SEO·검색 지표 수집기가 연결되면 실행 이력이 여기에 표시됩니다.</NotReady>
      ) : screen === "machines" ? (
        <AdminMachines onOpen={(m) => openMachine(m)} />
      ) : screen === "deploy" ? (
        <ReleaseManagementScreen />
      ) : screen === "billing-ops" ? (
        <AdminBillingScreen me={me} enter={enter} />
      ) : screen === "monitoring" ? (
        <AdminMonitoringScreen />
      ) : screen === "llm" ? (
        <LlmUsageScreen onOpenRun={(id, customerId) => setRunId(id, customerId || null)} />
      ) : screen === "usage" ? (
        <>
          <UsageAll llm={false} enter={enter} go={go} me={me} />
          <AdminUsageCompare />
        </>
      ) : screen === "audit" ? (
        <AdminAudit />
      ) : screen === "staff" ? (
        <StaffScreen open={staffOpen} setOpen={setStaffOpen} />
      ) : screen === "manuscripts" ? (
        // 요청서 29·31절: 원고함 새 원고 창의 "정형화해서 올리기" 채널(화면 안 공통 창), 만든 작업만 정형화 탭에서 보기(?ids=),
        // 등록된 원고의 "출처: 정형화" 링크, 정형화에서 등록한 원고 바로 열기 (?manuscript=)
        <ManuscriptLibraryScreen
          key={urlParam("manuscript") ?? "library"}
          me={me}
          initialDocumentId={urlParam("manuscript") ?? undefined}
          onOpenRefine={(id) => go("refine", { id })}
          onOpenRefineList={(ids) => go("refine", ids.length ? { ids: ids.join(",") } : {})}
          onOpenAiSettings={() => go("ai-execution")}
        />
      ) : screen === "refine" ? (
        <RefineScreen key={window.location.search} me={me} onOpenManuscript={(id) => go("manuscripts", { manuscript: id })} onOpenSettings={() => go("ai-execution")} />
      ) : screen === "guidelines" ? (
        <GuidelinesScreen me={me} />
      ) : screen === "ai-execution" ? (
        <AiExecutionScreen me={me} onOpenKeys={() => go("ai-keys")} />
      ) : screen === "ai-keys" ? (
        <LlmKeysScreen me={me} onUsage={() => void navigate({ to: "/admin/$screen", params: { screen: "llm" } })} />
      ) : screen === "roles" ? (
        <RolesScreen me={me} />
      ) : screen === "templates" ? (
        <TemplatesScreen />
      ) : screen === "collectors" ? (
        <SeoCollectorSettings me={me} onUsage={() => go("seo-collection")} />
      ) : screen === "settings" ? (
        <>
          <SettingsScreen me={me} />
          <ReviewPolicyPanel me={me} />
        </>
      ) : screen === "profile" ? (
        <ProfileScreen me={me} />
      ) : null}
      <CreateTaskDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id, customerId) => id && setRunId(id, customerId)} />
      <CustomerDetailDialog
        me={me}
        id={detail.id}
        initialTab={detail.tab}
        initialSeoTab={detail.seoTab}
        onClose={() => detail.close()}
        onEnter={(id) => {
          detail.close();
          enter(id);
        }}
        onMachine={(customerId, machineId) => openMachine({ id: machineId, customerId }, true)}
        onOpenRun={(id, customerId) => {
          // 시안 951f2f6: 요청 상세와 고객사 상세는 겹치지 않는다. 상세를 숨기고 "목록으로"에서 발행 요청 탭으로 돌아온다.
          const tab = detail.tab;
          detail.close();
          setRunId(id, customerId, () => {
            setRunId(null);
            detail.open(customerId, tab === "개요" ? "발행 요청" : tab);
          });
        }}
      />
      <MachineDetailDialog
        me={me}
        customerId={machine?.customerId ?? ""}
        machineId={machine?.machineId ?? null}
        customerStatus={(machine?.customerStatus ?? "active") as never}
        label={machine?.label}
        onClose={() => {
          // 실행 상세에서 넘어온 머신 상세를 닫으면 실행 상세도 닫는다 (둘은 겹쳐 뜨지 않음).
          if (machine?.fromRun) setRunId(null);
          setMachine(null);
        }}
        onBack={machine?.back || machine?.fromRun ? () => setMachine(null) : undefined}
        backLabel={machine?.fromRun ? "← 실행 상세로" : undefined}
        onOpenTask={(runId) => {
          if (!runId) return;
          setRunId(runId, machine?.customerId ?? null);
          setMachine(null);
        }}
      />
      <RunDetailDialog
        runId={run?.id ?? null}
        customerId={run?.customerId ?? null}
        onBack={run?.back}
        hidden={!!machine?.fromRun}
        onMachine={(m) => {
          const c = (customers.data?.items ?? []).find((x) => x.id === m.customerId);
          setMachine({ customerId: m.customerId, machineId: m.machineId, label: m.label ?? undefined, customerStatus: c?.status ?? "active", fromRun: true });
        }}
        onClose={(active) => {
          if (active) setToast("작업은 계속 진행됩니다 · 실행 이력에서 다시 볼 수 있습니다");
          setRunId(null);
        }}
        onReview={(taskId) => {
          useReviewFocus.getState().focus(taskId);
          setRunId(null);
          go("reviews");
        }}
      />
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
    </ConsoleShell>
  );
}

function AdminOverview({
  me,
  go,
  onOpenRun,
  onOpenCustomer,
  onOpenMachine,
}: {
  me: Me;
  go: (s: string, search?: Record<string, string>) => void;
  onOpenRun: (id: string, customerId: string | null) => void;
  onOpenCustomer: (id: string, tab?: string) => void;
  onOpenMachine: (m: { id: string; customerId: string; label?: string }) => void;
}) {
  // 시안 상황판: 실행 큐에 수집 상태, 아래에 운영 기록(감사 기록 최근 6건).
  const seoSummary = useSeoCollectSummary();
  const recentAudit = useAdminAudit(0);
  const o = useOverview();
  const customers = useCustomers();
  const machines = useAdminMachines();
  const failed = useAdminRuns({ status: "failed,dead", limit: 6 });
  const pendingPublish = useAdminRuns({ origin: "customer", status: "held", errorKind: "approval_required", limit: 6 }, can(me, "TASK_DETAIL"));
  if (o.isLoading) return <Loading />;
  if (o.error || !o.data) return <ErrorBox error={errorMessage(o.error)} onRetry={() => void o.refetch()} />;
  const d = o.data;
  const counts = d.customers ?? {};
  const totalCustomers = Object.values(counts).reduce((a, b) => a + b, 0);
  const offline = (machines.data?.items ?? []).filter((m) => m.pairedAt && m.status === "active" && !m.connected);
  const list = customers.data?.items ?? [];
  // 카드를 누르면 그 숫자의 목록으로 (2026-10-21 사용자). 볼 권한이 없는 목록은 연결하지 않는다.
  const runsOk = can(me, "TASK_DETAIL") && !isBillingOnly(me);
  return (
    <>
      <DataHeading title="운영 현황" />
      <StatGrid
        links={{
          고객사: { onOpen: () => go("customers", { status: "all" }), label: "고객사 목록에서 보기" },
          "온라인 머신": !isBillingOnly(me) && { onOpen: () => go("machines", offline.length ? { filter: "disconnected" } : {}), label: offline.length ? "연결 끊긴 머신 보기" : "머신 목록에서 보기" },
          "실패 실행 · 24h": runsOk && { onOpen: () => go("runs", { status: "failed,dead", scope: "all", period: "24h" }), label: "최근 24시간 실패 실행 보기" },
          대기: runsOk && { onOpen: () => go("runs", { status: ACTIVE_FILTER, scope: "all" }), label: "진행·대기 중인 실행 보기" },
          "확인 필요": runsOk && { onOpen: () => go("runs", { origin: "pending", scope: "all" }), label: "승인 대기 중인 고객 발행 요청 보기" },
        }}
        items={[
          ["고객사", totalCustomers, `정상 ${counts.active ?? 0} · 유예 ${counts.overdue ?? 0} · 정지 ${counts.suspended ?? 0}`],
          ["온라인 머신", `${d.machinesOnline} / ${d.machinesPaired}`, offline.length ? `${offline.length}대 현장 조치 필요` : "연결 상태 정상", offline.length ? "amber" : "teal"],
          ["실패 실행 · 24h", d.runsFailed24h, `성공 ${d.runsSucceeded24h}건`, d.runsFailed24h ? "coral" : "teal"],
          ["대기", d.tasksQueued + d.runsHeld, `진행 중 ${d.tasksRunning} · 보류 ${d.runsHeld}`],
          ...(can(me, "TASK_DETAIL") ? [["확인 필요", d.publishPending ?? 0, `고객 발행 요청 확인 대기${d.publishUnread ? ` · 새 답글 ${d.publishUnread}건` : ""}`, (d.publishPending ?? 0) > 0 ? "amber" : "teal"] as [string, number, string, "amber" | "teal"]] : []),
        ]}
      />
      <div className="meta-crew-grid-main">
        <div>
          {can(me, "TASK_DETAIL") && <AdminActiveRuns onOpen={(c) => c.runId && onOpenRun(c.runId, c.customerId)} />}
          <Panel title="지금 필요한 조치" extra={<Badge tone={offline.length + (failed.data?.total ?? 0) + (pendingPublish.data?.total ?? 0) ? "amber" : "teal"}>{offline.length + (failed.data?.total ?? 0) + (pendingPublish.data?.total ?? 0)}건</Badge>}>
            {(pendingPublish.data?.items ?? []).map((r) => (
              <InfoRow key={r.id} title={`${r.customerName ?? ""} · 발행 요청 확인 대기`} description={`${jobTitle(r.jobName)} · v${r.revision ?? 1} · ${fmtDateTime(r.createdAt)}${r.unreadForStaff ? ` · 새 답글 ${r.unreadForStaff}` : ""}`}>
                <Badge tone="amber">{publishLabel(r)[0]}</Badge>
                <Button size="sm" variant="secondary" onClick={() => onOpenRun(r.id, r.customerId)}>
                  확인
                </Button>
              </InfoRow>
            ))}
            {offline.map((m) => (
              <InfoRow key={m.id} title={`${m.customerName} · ${m.label} 응답 없음`} description={`마지막 수신 ${fmtDateTime(m.lastSeenAt)} · 현장 전원과 네트워크 확인`}>
                <Button size="sm" variant="secondary" onClick={() => onOpenMachine({ id: m.id, customerId: m.customerId, label: m.label })}>
                  머신 확인
                </Button>
              </InfoRow>
            ))}
            {(failed.data?.items ?? []).map((r) => (
              <InfoRow key={r.id} title={`${r.customerName ?? "시스템"} · ${jobTitle(r.jobName)}`} description={r.errorMessage ?? r.status}>
                <Button size="sm" variant="secondary" onClick={() => onOpenRun(r.id, r.customerId)}>
                  실행 확인
                </Button>
              </InfoRow>
            ))}
            {offline.length === 0 && (failed.data?.items ?? []).length === 0 && (pendingPublish.data?.items ?? []).length === 0 && <InfoRow title="조치할 항목이 없습니다" description="머신 연결과 최근 실행이 모두 정상입니다." />}
          </Panel>
          <Panel
            title="고객사"
            extra={
              <Button size="sm" variant="ghost" onClick={() => go("customers")}>
                전체 보기
              </Button>
            }
          >
            {list.slice(0, 8).map((c) => (
              <InfoRow key={c.id} title={c.name} description={`${c.stats?.plan?.planName ?? "플랜 없음"} · 머신 ${c.stats?.machineCount ?? 0}대 · 디바이스 ${c.stats?.deviceCount ?? 0}대`}>
                <Badge tone={serviceTone[c.status]}>{serviceNames[c.status]}</Badge>
                <Button size="sm" variant="ghost" onClick={() => onOpenCustomer(c.id)}>
                  상세 <ChevronRight size={14} />
                </Button>
              </InfoRow>
            ))}
            {list.length === 0 && (
              <InfoRow title="등록된 고객사가 없습니다" description="고객사 메뉴에서 첫 고객사를 등록하세요.">
                {can(me, "CUSTOMER_MANAGE") && (
                  <Button size="sm" onClick={() => go("customers")}>
                    고객사 등록
                  </Button>
                )}
              </InfoRow>
            )}
          </Panel>
        </div>
        <div>
          <Panel title="실행 큐">
            <InfoRow title="디바이스 작업" description={`대기 ${d.tasksQueued}건 · 진행 중 ${d.tasksRunning}건`}>
              <Badge tone="teal">정상</Badge>
            </InfoRow>
            <InfoRow title="보류된 실행" description="고객사 상태·월 한도 때문에 멈춘 실행 (풀리면 자동 재개)">
              <Badge tone={d.runsHeld ? "amber" : "teal"}>{d.runsHeld}건</Badge>
            </InfoRow>
            <InfoRow title="준비된 디바이스" description="승인되어 작업을 받을 수 있는 폰">
              <Badge>{d.devicesReady}대</Badge>
            </InfoRow>
            <InfoRow title="SEO 수집" description={seoSummary.data ? `추적 ${seoSummary.data.tracking}개 · 오늘 ${seoSummary.data.collectedToday}개 수집 · 연결 문제 ${seoSummary.data.connectionsProblem}건` : "코어 스케줄러 · 네이버 검색 API"}>
              <Badge tone={seoSummary.data?.failing ? "amber" : "teal"}>{seoSummary.data ? `실패 ${seoSummary.data.failing}건` : "—"}</Badge>
            </InfoRow>
          </Panel>
          <Panel title="운영 기록">
            {(recentAudit.data?.items ?? []).slice(0, 6).map((a, i) => (
              <InfoRow key={`${a.at}-${i}`} title={`${fmtTime(a.at)} · ${a.action}`} description={`${a.actorName ?? (a.actorKind === "system" ? "시스템" : a.actorKind)}${a.targetType ? ` · ${a.targetType}` : ""}`} />
            ))}
            {(recentAudit.data?.items ?? []).length === 0 && <InfoRow title="기록 없음" description="관리자·시스템의 동작이 감사 기록으로 남으면 여기에 보입니다." />}
            <div className="mcc-padding">
              <Button size="sm" variant="ghost" onClick={() => go("audit")}>
                감사 기록 전체 <ChevronRight size={14} />
              </Button>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}

function CustomersScreen({ enter, registerOpen, setRegisterOpen, onDetail }: { enter: (id: string) => void; registerOpen: boolean; setRegisterOpen: (v: boolean) => void; onDetail: (id: string) => void }) {
  const [search, setSearch] = useState("");
  // 기본은 정상 이용만 (2026-10-20 사용자 요청). "전체"로 바꾸면 모두. KPI 카드에서 오면 ?status= (all = 전체).
  const [status, setStatus] = useState(() => {
    const v = urlParam("status");
    return v === "all" ? "" : (v ?? "active");
  });
  const q = useCustomers();
  const rows = (q.data?.items ?? []).filter((c) => c.name.includes(search) && (!status || (status === "sanctioned" ? c.sanctionLevel > 0 : c.status === status)));
  return (
    <>
      <FilterBar>
        <SearchField showLabel label="고객사 검색" placeholder="고객사 이름 입력" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select label="고객사 상태" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">전체</option>
          <option value="active">정상 이용</option>
          <option value="overdue">결제 유예</option>
          <option value="suspended">서비스 정지</option>
          <option value="terminated">해지</option>
          <option value="sanctioned">제재 중</option>
        </Select>
      </FilterBar>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
      ) : rows.length === 0 ? (
        <Empty>{search ? "검색한 고객사가 없습니다." : "등록된 고객사가 없습니다."}</Empty>
      ) : (
        <div className="mcc-customers">
          {rows.map((c) => (
            <CustomerCard key={c.id} c={c} onDetail={() => onDetail(c.id)} />
          ))}
        </div>
      )}
      <RegisterCustomerDialog open={registerOpen} onClose={() => setRegisterOpen(false)} onCreated={(id) => onDetail(id)} />
    </>
  );
}

function CustomerCard({ c, onDetail }: { c: CustomerListItem; onDetail: () => void }) {
  const s = c.stats;
  return (
    <Panel title={c.name} extra={<Badge tone={serviceTone[c.status]}>{serviceNames[c.status]}</Badge>}>
      <div className="mcc-padding">
        <div className="mcc-customer-meta">
          <div className="mcc-avatar">{initials(c.name)}</div>
          <div>
            <strong>
              {s?.plan ? `${s.plan.planName} · ${s.plan.period === "year" ? "연간" : "월간"}` : "플랜 없음"}
              {s?.plan ? ` · ${fmtDate(s.plan.currentPeriodEnd)}까지` : ""}
            </strong>
            <small>{s?.contact ? `${s.contact.name}${s.contact.phone ? ` · ${s.contact.phone}` : ""}` : "담당자 미등록"}</small>
          </div>
        </div>
        <SummaryMetrics
          density="compact"
          items={[
            { label: "머신", value: `${s?.machinesOnline ?? 0}/${s?.machineCount ?? 0}`, icon: <Monitor />, tone: "blue" },
            { label: "디바이스", value: s?.deviceCount ?? 0, icon: <Smartphone />, tone: "teal" },
            { label: "월 작업", value: `${num(c.quota.used)}${c.quota.tasksPerMonth ? `/${num(c.quota.tasksPerMonth)}` : ""}`, icon: <Activity />, tone: "blue" },
          ]}
        />
        <div className="meta-crew-buttons">
          <Button size="sm" onClick={onDetail}>
            상세 보기 <ArrowUpRight size={14} />
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function RegisterCustomerDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const formId = useId();
  const [error, setError] = useState<string | null>(null);
  const create = useAction(async (b: { name: string; bizNo?: string; planCode: string; contact: { name: string; phone?: string; email?: string; jobTitle?: string } | null }) => {
    const c = await api.post<{ id: string }>("/admin/customers", { name: b.name, bizNo: b.bizNo, planCode: b.planCode, planPeriod: b.planCode === "promo" ? "year" : "month" });
    if (b.contact) await api.post(`/admin/customers/${c.id}/contacts`, { ...b.contact, role: "onsite" });
    return c;
  }, [["admin"]]);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="고객사 등록"
      description="등록하면 선택한 플랜으로 바로 시작합니다. 체험은 기간이 끝나면 자동으로 정지됩니다."
      footer={<DialogActions secondary={{ label: "취소", onClick: onClose }} primary={{ label: "등록", type: "submit", form: formId, disabled: create.isPending }} note={error ?? undefined} />}
    >
      <form
        id={formId}
        className="mcc-form"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const d = new FormData(e.currentTarget);
          const contactName = String(d.get("contactName") ?? "").trim();
          create
            .mutateAsync({
              name: String(d.get("name")).trim(),
              bizNo: String(d.get("bizNo") ?? "").trim() || undefined,
              planCode: String(d.get("planCode")),
              contact: contactName
                ? { name: contactName, phone: String(d.get("contactPhone") ?? "").trim() || undefined, email: String(d.get("contactEmail") ?? "").trim() || undefined, jobTitle: jobTitleOf(d) || undefined }
                : null,
            })
            .then((c) => {
              onClose();
              onCreated(c.id);
            })
            .catch((er) => setError(errorMessage(er)));
        }}
      >
        <Field name="name" label="고객사 이름" required maxLength={100} />
        <Field name="bizNo" label="사업자등록번호" placeholder="선택" />
        <Field name="contactName" label="담당자 이름" required />
        <Field name="contactPhone" label="담당자 연락처" />
        <Field name="contactEmail" label="담당자 이메일" type="email" />
        <JobTitleField />
        <Select label="초기 플랜" name="planCode" defaultValue="trial">
          <option value="trial">체험</option>
          <option value="basic">기본</option>
          <option value="pro">프로</option>
          <option value="promo">프로모션 (프로 1년 무료)</option>
        </Select>
      </form>
    </Dialog>
  );
}

function AdminRuns({ onOpenRun }: { onOpenRun: (id: string, customerId: string | null) => void }) {
  const active = useActiveTasks();
  // KPI 카드에서 오면 ?status= · ?origin= 로 첫 필터를 잡는다
  const [status, setStatus] = useState(() => urlParam("status") ?? "");
  const [customerId, setCustomerId] = useState("");
  const [origin, setOrigin] = useState(() => urlParam("origin") ?? "");
  const [offset, setOffset] = useState(0);
  // 2026-10-16 고객 발행: "고객 요청"·"승인 대기" 빠른 필터.
  const originParams = origin === "pending" ? { origin: "customer", status: "held", errorKind: "approval_required" } : origin ? { origin } : {};
  const extra = useRunExtraFilters(() => setOffset(0));
  const q = useAdminRuns({ ...runFilterParams(status), ...originParams, ...extra.params, customerId, offset, limit: 50 });
  const customers = useCustomers();
  const machines = useAdminMachines();
  return (
    <RunTable
      admin
      activeCount={active.data?.total}
      machines={(machines.data?.items ?? []).map((m) => ({ id: m.id, label: m.customerName ? `${m.label} · ${m.customerName}` : m.label }))}
      extraFilters={
        <>
        {extra.node}
        <Select
          label="출처"
          value={origin}
          onChange={(e) => {
            setOrigin(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">전체</option>
          <option value="customer">고객 요청</option>
          <option value="pending">승인 대기</option>
        </Select>
        </>
      }
      rows={q.data?.items ?? []}
      total={q.data?.total ?? 0}
      offset={offset}
      limit={50}
      onOffset={setOffset}
      status={status}
      onStatus={(s) => {
        setStatus(s);
        setOffset(0);
      }}
      customers={(customers.data?.items ?? []).map((c) => ({ id: c.id, name: c.name }))}
      customerId={customerId}
      onCustomer={(id) => {
        setCustomerId(id);
        setOffset(0);
      }}
      loading={q.isLoading}
      error={q.error ? errorMessage(q.error) : null}
      onRetry={() => void q.refetch()}
      onOpen={(r) => onOpenRun(r.id, r.customerId)}
    />
  );
}

function AdminMachines({ onOpen }: { onOpen: (m: { id: string; customerId: string; label?: string }) => void }) {
  const q = useAdminMachines();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  return <FilteredMachineCards rows={q.data?.items ?? []} onOpen={onOpen} showCustomer deletedUrl="/admin/machines?deleted=1" />;
}

function BillingOps({ me: _me, enter: _enter }: { me: Me; enter: (id: string, s?: string) => void }) {
  const customers = useCustomers();
  const plans = usePlans();
  const setDetail = (id: string) => useCustomerDetailFocus.getState().open(id, "플랜·기간");
  const list = customers.data?.items ?? [];
  const soon = list.filter((c) => c.stats?.plan && new Date(c.stats.plan.currentPeriodEnd).getTime() - Date.now() < 7 * 86400_000);
  return (
    <>
      <StatGrid
        items={[
          ["고객사", list.length, "구독 중"],
          ["유예 고객사", list.filter((c) => c.status === "overdue").length, "결제 확인 필요", "amber"],
          ["정지 고객사", list.filter((c) => c.status === "suspended").length, "새 작업 중지", "coral"],
          ["구독 기간", soon.length, "7일 안에 끝나는 구독"],
        ]}
      />
      <Panel title="고객사 구독">
        <Table>
          <thead>
            <tr>
              <th>고객사</th>
              <th>플랜</th>
              <th>기간 종료</th>
              <th>이번 달 작업</th>
              <th>상태</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.stats?.plan ? `${c.stats.plan.planName} · ${c.stats.plan.period === "year" ? "연간" : "월간"}` : "-"}</td>
                <td>{fmtDate(c.stats?.plan?.currentPeriodEnd)}</td>
                <td>
                  {num(c.quota.used)}
                  {c.quota.tasksPerMonth ? ` / ${num(c.quota.tasksPerMonth)}` : ""}
                </td>
                <td>
                  <Badge tone={serviceTone[c.status]}>{serviceNames[c.status]}</Badge>
                </td>
                <td>
                  <Button size="sm" variant="ghost" onClick={() => setDetail(c.id)}>
                    관리
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
      <Panel title="플랜 (현재 버전)">
        {(plans.data?.items ?? []).map((p) => (
          <InfoRow
            key={`${p.planCode}-${p.period}`}
            title={`${p.name} · ${p.period === "year" ? "연간" : "월간"} · v${p.version}`}
            description={`${num(p.price)}원 · 월 작업 ${p.includes.tasksPerMonth ?? "무제한"}건 · 머신 ${p.includes.machines ?? "무제한"} · 디바이스 ${p.includes.devices ?? "무제한"}`}
          />
        ))}
      </Panel>
      <NotReady title="결제 수단·청구서·입금 승인">PG·자동이체 결제, 청구서 발행, 계좌이체 승인, 환불은 결제 연동 후 제공됩니다. 지금은 관리 정보에서 플랜과 기간을 직접 지정합니다.</NotReady>
    </>
  );
}

function UsageAll({ llm, enter, go, me }: { llm: boolean; enter: (id: string, s?: string) => void; go?: (s: string, search?: Record<string, string>) => void; me?: Me }) {
  const q = useCustomers();
  const list = q.data?.items ?? [];
  const sum = list.reduce(
    (a, c) => ({
      used: a.used + c.quota.used,
      input: a.input + c.quota.tokens.input,
      cached: a.cached + c.quota.tokens.cached,
      output: a.output + c.quota.tokens.output,
      reasoning: a.reasoning + c.quota.tokens.reasoning,
    }),
    { used: 0, input: 0, cached: 0, output: 0, reasoning: 0 },
  );
  return (
    <>
      <StatGrid
        links={{
          "진행 중": !!go && can(me, "TASK_DETAIL") && { onOpen: () => go("runs", { status: ACTIVE_FILTER, scope: "all" }), label: "진행·대기 중인 실행 보기" },
          고객사: !!go && { onOpen: () => go("customers", { status: "all" }), label: "고객사 목록에서 보기" },
        }}
        items={[
          ["LLM 작업", num(sum.used), "이번 달 · 산입 기준"],
          ["토큰 사용", compact(sum.input + sum.output + sum.reasoning), `입력 ${compact(sum.input)} · 캐시 ${compact(sum.cached)}`],
          ["진행 중", list.reduce((a, c) => a + c.quota.inFlight, 0), "대기·진행 작업"],
          ["고객사", list.length, "집계 대상"],
        ]}
      />
      <Panel title={llm ? "고객사별 토큰 사용 · 이번 달" : "고객사별 이번 달 한도"}>
        {list.map((c) =>
          llm ? (
            <InfoRow
              key={c.id}
              title={c.name}
              description={`입력 ${num(c.quota.tokens.input)} · 캐시 ${num(c.quota.tokens.cached)} · 출력 ${num(c.quota.tokens.output)} · 추론 ${num(c.quota.tokens.reasoning)}`}
            >
              <Button size="sm" variant="ghost" onClick={() => enter(c.id, "usage")}>
                열기
              </Button>
            </InfoRow>
          ) : (
            <InfoRow key={c.id} title={c.name} description={`${num(c.quota.used)}${c.quota.tasksPerMonth ? ` / ${num(c.quota.tasksPerMonth)}` : ""}건`}>
              {c.quota.tasksPerMonth ? <progress aria-label={`${c.name} 작업 한도`} value={c.quota.used} max={c.quota.tasksPerMonth} /> : null}
              {c.quota.tasksPerMonth ? <Badge tone={c.quota.used / c.quota.tasksPerMonth >= 0.8 ? "amber" : "teal"}>{Math.round((c.quota.used / c.quota.tasksPerMonth) * 100)}%</Badge> : <Badge>무제한</Badge>}
            </InfoRow>
          ),
        )}
      </Panel>
    </>
  );
}

function AdminAudit() {
  const [offset, setOffset] = useState(0);
  const q = useAdminAudit(offset);
  return <AuditTable showCustomer rows={q.data?.items ?? []} total={q.data?.total ?? 0} offset={offset} onOffset={setOffset} loading={q.isLoading} error={q.error ? errorMessage(q.error) : null} />;
}

/** 직원 목록. 초대는 페이지 FAB (직원 역할만 고를 수 있다). */
function StaffScreen({ open, setOpen }: { open: boolean; setOpen: (v: boolean) => void }) {
  const q = useStaff();
  const me = useAuth((s) => s.me);
  const roles = useGlobalRoles();
  const staffRoleOptions = (roles.data?.roles ?? []).filter((r) => r.scope === "staff");
  const manage = !!me && me.permissions.includes("USER_MANAGE");
  const [assign, setAssign] = useState<Member | null>(null);
  return (
    <Panel title="관리자·전역 역할">
      {q.isLoading ? (
        <Loading />
      ) : (
        <Table>
          <thead>
            <tr>
              <th>이름</th>
              <th>이메일</th>
              <th>직책</th>
              <th>역할</th>
              <th>상태</th>
              <th>마지막 로그인</th>
              {manage && <th aria-label="관리" />}
            </tr>
          </thead>
          <tbody>
            {(q.data?.items ?? []).map((m) => (
              <tr key={m.id}>
                <td>
                  <MaskedValue value={m.name} source={{ url: `/admin/members/${m.id}/pii`, pick: (d) => d.name }} />
                </td>
                <td>
                  <MaskedValue value={m.email} source={{ url: `/admin/members/${m.id}/pii`, pick: (d) => d.email }} />
                </td>
                <td>{m.jobTitle || "-"}</td>
                <td>
                  <RoleChips roles={m.roles} />
                </td>
                <td>
                  <Badge tone={m.status === "active" ? "teal" : "neutral"}>{m.status === "active" ? "활성" : "중지"}</Badge>
                </td>
                <td>{m.lastLoginAt ? fmtDateTime(m.lastLoginAt) : "-"}</td>
                {manage && (
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => setAssign(m)}>
                      역할 지정
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <AddMemberDialog open={open} onClose={() => setOpen(false)} path="/admin/members" roleOptions={staffRoleOptions} defaultHint="고르지 않으면 열람" invalidate={keys.staff} />
      <AssignRolesDialog
        open={!!assign}
        onClose={() => setAssign(null)}
        memberName={assign?.name ?? ""}
        options={staffRoleOptions}
        current={assign?.roles ?? []}
        path={`/admin/users/${assign?.id ?? ""}/roles`}
        invalidate={[[...keys.staff]]}
      />
    </Panel>
  );
}

function TemplatesScreen() {
  const q = useTemplates();
  return (
    <Panel title="시스템 템플릿">
      {(q.data?.items ?? []).map((t) => (
        <InfoRow key={t.taskType} title={`${t.name ?? t.taskType} · v${t.version}`} description={t.taskType}>
          <details className="mcc-details">
            <summary>내용</summary>
            <pre className="mcc-pre">{t.systemPrompt}</pre>
          </details>
        </InfoRow>
      ))}
      {q.isSuccess && (q.data?.items ?? []).length === 0 && <InfoRow title="템플릿이 없습니다" />}
    </Panel>
  );
}
